import { useCallback, useEffect, useState } from 'react';
import { api, ApiFailure, type DatasetSummary, type Dataset, type Report, type Bucket, type Aggregation } from '../api';
import { Notice } from './Panels';
import { CategoryChart } from './Charts';
import type { VizColors } from '../theme';

interface SourceState {
  dataset: Dataset;
  report: Report;
  chartData: Record<string, Bucket[]>;
}

/**
 * Multi-Source Unified Dashboard.
 *
 * Compare metrics across multiple datasets in a single view. Select two or more
 * datasets, pick a measure, and see them side by side — same aggregation, same
 * chart, so the comparison is honest.
 *
 * This is the feature that turns DataVantage from a single-file analyser into
 * a multi-platform comparison tool.
 */
export function MultiSource({ colors }: { colors: VizColors }) {
  const [datasets, setDatasets] = useState<DatasetSummary[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sources, setSources] = useState<Map<string, SourceState>>(new Map());
  const [measure, setMeasure] = useState<string>('');
  const [aggregation, setAggregation] = useState<Aggregation>('sum');
  const [groupBy, setGroupBy] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    api.listDatasets().then((r) => setDatasets(r.datasets)).catch(() => setDatasets([]));
  }, []);

  const toggleSource = useCallback(async (id: string) => {
    setFailure(null);
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        setSources((s) => { const next = new Map(s); next.delete(id); return next; });
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });

    if (!selectedIds.includes(id)) {
      setBusy(true);
      try {
        const [datasetResp, reportResp] = await Promise.all([
          api.getDataset(id),
          api.listReports().then((r) => r.reports.find((rep) => rep.datasetId === id)),
        ]);

        if (reportResp) {
          const chartData: Record<string, Bucket[]> = {};
          for (const chart of reportResp.charts.slice(0, 4)) {
            try {
              const result = await api.query(id, {
                groupBy: chart.x,
                measure: chart.y,
                aggregation: chart.aggregation,
                limit: 20,
              });
              chartData[chart.id] = result.result.buckets;
            } catch { /* skip charts that fail */ }
          }

          setSources((prev) => {
            const next = new Map(prev);
            next.set(id, { dataset: datasetResp.dataset, report: reportResp, chartData });
            return next;
          });

          // Auto-select measure and groupBy from first source
          if (sources.size === 0) {
            const cols = datasetResp.dataset.columns;
            const numericCol = cols.find((c) => c.type === 'numeric' && !c.isIdentifier);
            const catCol = cols.find((c) => c.type === 'categorical');
            if (numericCol) setMeasure(numericCol.name);
            if (catCol) setGroupBy(catCol.name);
          }
        }
      } catch (e) {
        setFailure(e instanceof ApiFailure ? e : new ApiFailure('Could not load dataset.', 'Try again.', 0));
      } finally {
        setBusy(false);
      }
    }
  }, [selectedIds, sources.size]);

  // Cross-source comparison query
  const [comparisonData, setComparisonData] = useState<Map<string, Bucket[]>>(new Map());

  useEffect(() => {
    if (!measure || !groupBy || selectedIds.length === 0) {
      setComparisonData(new Map());
      return;
    }

    const load = async () => {
      const data = new Map<string, Bucket[]>();
      for (const id of selectedIds) {
        try {
          const result = await api.query(id, { groupBy, measure, aggregation, limit: 20 });
          data.set(id, result.result.buckets);
        } catch { /* skip */ }
      }
      setComparisonData(data);
    };
    void load();
  }, [measure, groupBy, aggregation, selectedIds]);

  const allColumns = selectedIds.length > 0
    ? [...new Set(selectedIds.flatMap((id) => {
        const src = sources.get(id);
        return src ? src.dataset.columns.map((c) => c.name) : [];
      }))]
    : [];

  const numericColumns = selectedIds.length > 0
    ? allColumns.filter((col) => {
        return selectedIds.every((id) => {
          const src = sources.get(id);
          const c = src?.dataset.columns.find((x) => x.name === col);
          return c?.type === 'numeric' && !c.isIdentifier;
        });
      })
    : [];

  const categoricalColumns = selectedIds.length > 0
    ? allColumns.filter((col) => {
        return selectedIds.every((id) => {
          const src = sources.get(id);
          const c = src?.dataset.columns.find((x) => x.name === col);
          return c?.type === 'categorical';
        });
      })
    : [];

  return (
    <div className="page">
      <header className="intro">
        <h2>Multi-Source Comparison</h2>
        <p>
          Compare metrics across multiple datasets. Select two or more sources, pick a shared measure
          and dimension, and see them side by side.
        </p>
      </header>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {/* Source selection */}
      <section className="card">
        <header>
          <h2>Select sources</h2>
          <span className="sub">{selectedIds.length} selected</span>
        </header>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {datasets.map((ds) => (
            <button
              key={ds.id}
              className={selectedIds.includes(ds.id) ? 'primary' : 'ghost'}
              onClick={() => void toggleSource(ds.id)}
              disabled={busy}
              style={{ fontSize: 13 }}
            >
              {ds.name}
              <span style={{ opacity: 0.6, marginLeft: 6, fontSize: 11 }}>
                {ds.rowCount.toLocaleString()} rows
              </span>
            </button>
          ))}
          {datasets.length === 0 && (
            <p className="muted">No datasets yet. Upload some files first.</p>
          )}
        </div>
      </section>

      {/* Comparison controls */}
      {selectedIds.length >= 2 && (
        <>
          <section className="card">
            <header><h2>Comparison settings</h2></header>
            <div className="row" style={{ marginTop: 12, gap: 16, flexWrap: 'wrap' }}>
              <label className="field">
                Measure
                <select value={measure} onChange={(e) => setMeasure(e.target.value)}>
                  <option value="">Select a measure…</option>
                  {numericColumns.map((col) => <option key={col} value={col}>{col}</option>)}
                </select>
              </label>
              <label className="field">
                Group by
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                  <option value="">Select a dimension…</option>
                  {categoricalColumns.map((col) => <option key={col} value={col}>{col}</option>)}
                </select>
              </label>
              <label className="field">
                Aggregation
                <select value={aggregation} onChange={(e) => setAggregation(e.target.value as Aggregation)}>
                  <option value="sum">Sum</option>
                  <option value="avg">Average</option>
                  <option value="count">Count</option>
                  <option value="min">Min</option>
                  <option value="max">Max</option>
                </select>
              </label>
            </div>
          </section>

          {/* Comparison charts */}
          {measure && groupBy && (
            <section className="card">
              <header>
                <h2>{aggregation}({measure}) by {groupBy}</h2>
                <span className="sub">{comparisonData.size} sources compared</span>
              </header>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(selectedIds.length, 3)}, 1fr)`, gap: 16, marginTop: 16 }}>
                {selectedIds.map((id) => {
                  const src = sources.get(id);
                  const buckets = comparisonData.get(id);
                  if (!src || !buckets || buckets.length === 0) return null;
                  return (
                    <div key={id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
                      <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>{src.dataset.name}</h3>
                      <p className="muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
                        {src.dataset.rowCount.toLocaleString()} rows · {src.dataset.columns.length} columns
                      </p>
                      <CategoryChart buckets={buckets} measureLabel={`${aggregation}(${measure})`} colors={colors} />
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Scorecard comparison */}
          {measure && (
            <section className="card">
              <header><h2>Scorecard comparison</h2></header>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(selectedIds.length, 4)}, 1fr)`, gap: 12, marginTop: 12 }}>
                {selectedIds.map((id) => {
                  const src = sources.get(id);
                  if (!src) return null;
                  const scorecard = src.report.scorecards.find((s) => s.derivedFrom.includes(measure));
                  const total = scorecard?.value ?? 0;
                  return (
                    <div key={id} style={{ background: 'var(--sunken)', borderRadius: 10, padding: 16 }}>
                      <div style={{ fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: '0.05em', color: 'var(--ink-muted)', marginBottom: 4 }}>
                        {src.dataset.name}
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
                        {scorecard?.formatted ?? typeof total === 'number' ? total.toLocaleString() : '—'}
                      </div>
                      {scorecard?.trend && (
                        <div style={{ fontSize: 12, color: scorecard.trend.direction === 'up' ? '#059669' : '#dc2626', marginTop: 2 }}>
                          {scorecard.trend.direction === 'up' ? '▲' : '▼'} {Math.abs(scorecard.trend.changePercent).toFixed(1)}%
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {selectedIds.length === 1 && (
        <div className="card empty">
          <h3>Select at least two sources</h3>
          <p>Add another dataset to start comparing metrics side by side.</p>
        </div>
      )}
    </div>
  );
}
